import { AUTH_SUBJECT_KEY } from './direct-auth';

/**
 * Which reader the persisted offline cache belongs to.
 *
 * A successful login used to discard that cache unconditionally
 * (`clearPersistStorage()` in `templates/poets/home/home.tsx`'s
 * `onSuccess`), which is right when a *different* person signs in on the
 * same device -- one reader must never be served another's downloads --
 * but wrong for the same reader signing in again, which is the common
 * case: it throws away every issue they had downloaded, for nothing.
 * Morningstar can't hit this at all (auth disabled, so it never logs in),
 * which is one of the few real behavioural differences between the two
 * when reading offline.
 *
 * The two are told apart by recording an identity alongside the cache and
 * comparing on the next sign-in.
 */
const CACHE_OWNER_KEY = 'cf-cache-owner';

/**
 * Best available stable identifier for the reader who is currently signed
 * in, or `null` when none can be determined.
 *
 * `AUTH_SUBJECT_KEY` first: it's the auth subject (`sub`) the login and JWT
 * session writers already persist, and it's the closest thing to a real
 * account id. Falls back to the `user` blob's own `uid`/`email` for tenants
 * whose flow populates that but not a subject.
 */
function currentReaderId(): string | null {
	const subject = localStorage.getItem(AUTH_SUBJECT_KEY);
	if (subject) {
		return subject;
	}
	try {
		const raw = localStorage.getItem('user');
		if (!raw) {
			return null;
		}
		const user = JSON.parse(raw) as { uid?: string; email?: string };
		return user.uid ?? user.email ?? null;
	} catch {
		// A malformed `user` blob tells us nothing about who this is.
		return null;
	}
}

/**
 * Whether the reader who just signed in is someone other than the one the
 * persisted cache was built for -- i.e. whether that cache has to go.
 *
 * Returns `true` when the answer can't be established (no identity now, or
 * none recorded against the existing cache). That's deliberate: discarding
 * a cache that might belong to someone else costs the current reader a
 * re-download, while keeping one that does belong to someone else would
 * serve them another person's content. The conservative branch is also
 * exactly the previous behaviour, so anything this can't identify is no
 * worse off than before.
 */
export function hasCacheOwnerChanged(): boolean {
	const current = currentReaderId();
	if (!current) {
		return true;
	}
	const previous = localStorage.getItem(CACHE_OWNER_KEY);
	if (!previous) {
		return true;
	}
	return previous !== current;
}

/**
 * Records the signed-in reader as the owner of the persisted cache, so the
 * next sign-in can tell "same reader again" from "someone else". A no-op
 * when there's no identity to record -- better to leave nothing than to
 * write a placeholder that a later comparison would treat as a real match.
 */
export function rememberCacheOwner(): void {
	const current = currentReaderId();
	if (!current) {
		return;
	}
	localStorage.setItem(CACHE_OWNER_KEY, current);
}

/**
 * Drops the recorded owner. For sign-out, which clears the cache anyway --
 * leaving the old owner behind would make the next sign-in by that same
 * reader look like a match against a cache that no longer exists.
 */
export function forgetCacheOwner(): void {
	localStorage.removeItem(CACHE_OWNER_KEY);
}
